// frontend/src/admin/pages/PromptManagementPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import {
  listPromptVersions,
  createPromptVersion,
  deployPromptVersion,
  getDeployStatus,
} from "../api";

const AGENTS = ["aria", "dev", "sam", "mara"];

function fmt(iso) {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function metric(v) {
  return v == null ? "—" : v;
}

/**
 * PromptManagementPage — version each agent's system prompt + few-shots, and
 * run a regression-and-deploy pass that promotes a candidate only if it beats
 * the active version (P18-G7/G8). Agent tabs on top; version list with metrics;
 * an editor to author a new (inactive) version; per-version deploy with polling.
 */
function PromptManagementPage() {
  const [agent, setAgent] = useState(AGENTS[0]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // version number whose body is shown

  const [systemPrompt, setSystemPrompt] = useState("");
  const [fewShots, setFewShots] = useState([]); // [{ user, assistant }]
  const [creating, setCreating] = useState(false);

  const [deployingId, setDeployingId] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVersions(await listPromptVersions(agent));
    } catch {
      toast.error("Failed to load prompt versions.");
    } finally {
      setLoading(false);
    }
  }, [agent]);

  useEffect(() => {
    load();
    return () => clearInterval(pollRef.current);
  }, [load]);

  const addShot = () =>
    setFewShots((s) => [...s, { user: "", assistant: "" }]);
  const removeShot = (i) =>
    setFewShots((s) => s.filter((_, idx) => idx !== i));
  const updateShot = (i, key, value) =>
    setFewShots((s) => s.map((x, idx) => (idx === i ? { ...x, [key]: value } : x)));

  const handleCreate = async () => {
    if (!systemPrompt.trim()) {
      toast.error("System prompt is required.");
      return;
    }
    setCreating(true);
    try {
      const cleanShots = fewShots.filter((s) => s.user.trim() && s.assistant.trim());
      await createPromptVersion(agent, {
        system_prompt: systemPrompt,
        few_shots: cleanShots,
      });
      toast.success("New version created (inactive).");
      setSystemPrompt("");
      setFewShots([]);
      load();
    } catch {
      toast.error("Failed to create version.");
    } finally {
      setCreating(false);
    }
  };

  const pollDeploy = (taskId) => {
    pollRef.current = setInterval(async () => {
      try {
        const s = await getDeployStatus(agent, taskId);
        if (s.status === "SUCCESS" || s.status === "FAILURE") {
          clearInterval(pollRef.current);
          setDeployingId(null);
          if (s.status === "SUCCESS" && s.result) {
            toast.success(
              s.result.activated
                ? `Deployed — new version is now active (win ${Math.round(
                    s.result.win_rate * 100,
                  )}%).`
                : `Kept current version (win ${Math.round(
                    (s.result.win_rate || 0) * 100,
                  )}%, loss ${Math.round((s.result.loss_rate || 0) * 100)}%).`,
            );
          } else {
            toast.error("Regression run failed.");
          }
          load();
        }
      } catch {
        clearInterval(pollRef.current);
        setDeployingId(null);
      }
    }, 2000);
  };

  const handleDeploy = async (version) => {
    setDeployingId(version.id);
    try {
      const { task_id } = await deployPromptVersion(agent, version.id);
      toast("Regression run started…");
      pollDeploy(task_id);
    } catch {
      toast.error("Failed to start regression.");
      setDeployingId(null);
    }
  };

  return (
    <div>
      <h1 className="mb-5 text-xl font-semibold text-slate-900">AI Prompts</h1>

      {/* Agent tabs */}
      <div className="mb-5 flex gap-1 border-b border-slate-200">
        {AGENTS.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAgent(a)}
            className={[
              "px-4 py-2 text-sm font-medium capitalize",
              a === agent
                ? "border-b-2 border-indigo-600 text-indigo-700"
                : "text-slate-500 hover:text-slate-700",
            ].join(" ")}
          >
            {a}
          </button>
        ))}
      </div>

      {/* Version list */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : versions.length === 0 ? (
        <p className="text-sm text-slate-500">No versions yet for {agent}.</p>
      ) : (
        <table className="mb-8 w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs text-slate-500">
              <th className="py-2 pr-2">Version</th>
              <th className="py-2 pr-2">CSAT</th>
              <th className="py-2 pr-2">Resolution</th>
              <th className="py-2 pr-2">Avg turns</th>
              <th className="py-2 pr-2">Created</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <React.Fragment key={v.id}>
                <tr className="border-b last:border-0">
                  <td className="py-2 pr-2 font-medium text-slate-800">
                    v{v.version}
                    {v.is_active && (
                      <span
                        data-testid={`active-badge-${v.version}`}
                        className="ml-2 rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
                      >
                        ACTIVE
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-slate-500">{metric(v.avg_csat)}</td>
                  <td className="py-2 pr-2 text-slate-500">
                    {metric(v.resolution_rate)}
                  </td>
                  <td className="py-2 pr-2 text-slate-500">{metric(v.avg_turns)}</td>
                  <td className="py-2 pr-2 text-slate-500">{fmt(v.created_at)}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setExpanded(expanded === v.version ? null : v.version)
                      }
                      className="mr-3 text-xs font-medium text-slate-500 hover:text-slate-700"
                    >
                      {expanded === v.version ? "Hide" : "View"}
                    </button>
                    {!v.is_active && (
                      <button
                        type="button"
                        onClick={() => handleDeploy(v)}
                        disabled={deployingId !== null}
                        className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {deployingId === v.id
                          ? "Running…"
                          : "Run regression & deploy"}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === v.version && (
                  <tr>
                    <td colSpan={6} className="bg-slate-50 px-3 py-3">
                      <pre className="whitespace-pre-wrap text-xs text-slate-700">
                        {v.system_prompt}
                      </pre>
                      {(v.few_shots || []).length > 0 && (
                        <div className="mt-2 space-y-1 text-xs">
                          {v.few_shots.map((s, i) => (
                            <div key={i} className="text-slate-600">
                              <strong>U:</strong> {s.user} · <strong>A:</strong>{" "}
                              {s.assistant}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* New version editor */}
      <div className="rounded-lg border border-slate-200 p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-800">
          New version for {agent}
        </h2>
        <textarea
          aria-label="System prompt"
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={6}
          placeholder="System prompt…"
          className="mb-3 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
        />
        <div className="mb-3 space-y-2">
          {fewShots.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input
                aria-label={`Few-shot user ${i + 1}`}
                value={s.user}
                onChange={(e) => updateShot(i, "user", e.target.value)}
                placeholder="User"
                className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
              <input
                aria-label={`Few-shot assistant ${i + 1}`}
                value={s.assistant}
                onChange={(e) => updateShot(i, "assistant", e.target.value)}
                placeholder="Assistant"
                className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
              <button
                type="button"
                onClick={() => removeShot(i)}
                aria-label={`Remove few-shot ${i + 1}`}
                className="px-2 text-slate-400 hover:text-rose-600"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addShot}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
          >
            + Add few-shot pair
          </button>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
        >
          {creating ? "Creating…" : "Create version"}
        </button>
      </div>
    </div>
  );
}

export default PromptManagementPage;
